import { CheckIcon, PencilIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { deleteRoverTdm, patchRoverTdm } from '@/api/Rover/applicationTDM';
import type { IGetDeliverablesResData, IPatchTdmPayload } from '@/api/Rover/applicationTDM/types';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import Input from '@/components/UI/Form/Input';
import Select from '@/components/UI/Form/Select';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationTeamMembers } from '@/hooks/ReactQueryHooks/useApplicationQuery';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import type { GetVendorsResponse } from '@/types/Vendor';
import { deliverableStatus } from '../../../../../../../constants/Project';
import { TdmReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import { useTitleById } from '../../../../../../../hooks/ReactQueryHooks/useTitlesQuery';
import { notifyUser } from '../../../../../../../store/Toast';
import type { TdmMemberInterface } from '../../../../../../../types/Tdm';
import { formatDate3, getInitials, isObjectEmpty, sanitizeResponseError } from '../../../../../../../utils';

interface DeliverableRowInterface extends IGetDeliverablesResData {
  isEditing: boolean;
}

export default function DueDiligenceDeliverableRow({
  deliverable,
  vendors,
}: {
  deliverable: IGetDeliverablesResData;
  vendors?: GetVendorsResponse[];
}) {
  const { applicationId, applicationFormId } = useParams();
  const navigate = useNavigate();
  const _location = useLocation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [inputErrors, setInputErrors] = useState({
    name: '',
    dueDate: '',
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [touchedInput, setTouchedInput] = useState<{
    [key: string]: boolean;
  }>({});

  const { getTitle } = useTitleById();

  const getApplicationTeamMembers = useApplicationTeamMembers({
    applicationId: applicationId!,
  });
  const applicationTeamMembers = getApplicationTeamMembers?.data?.data;
  const teams = applicationTeamMembers?.members?.map((member: any) => member?.user);

  const [deliverableRow, setDeliverableRow] = useState<DeliverableRowInterface>({
    ...deliverable,
    isEditing: false,
  });

  useEffect(() => {
    setDeliverableRow({ ...deliverable, isEditing: false });
  }, [deliverable]);

  const patchTdm = useMutation(
    (data: Partial<IPatchTdmPayload>) =>
      patchRoverTdm({
        tdmType: 'deliverable',
        tdmId: deliverableRow.id,
        data,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([TdmReactQueryKey.GET_APPLICATION_DELIVERABLES]);
        editRowHandler();
        setTouchedInput({});
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Deliverable saved successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const removeTdm = useMutation((tdmId: number) => deleteRoverTdm({ tdmType: 'deliverable', tdmId }), {
    onSuccess: () => {
      queryClient.invalidateQueries([TdmReactQueryKey.GET_APPLICATION_DELIVERABLES]);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Deliverable delete successful',
          toastType: 'success',
        })
      );
      setShowDeleteConfirm(false);
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const cancelEditHandler = () => {
    setDeliverableRow({ ...deliverable, isEditing: false });
  };

  const editRowHandler = () => {
    setDeliverableRow((row: DeliverableRowInterface) => ({
      ...row,
      isEditing: !row.isEditing,
    }));
    if (!deliverableRow?.dueDate) {
      setInputErrors((prevState) => ({
        ...prevState,
        dueDate: 'Due Date is required',
      }));
    }
  };

  const rowInputChangeHandler = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let newValue: any = e.target.value;

    if (e.target.name === 'lead' || e.target.name === 'support') {
      newValue = teams.find((mem: TdmMemberInterface) => mem.id === parseInt(newValue, 10));
    }

    if (e.target.name === 'vendor' && vendors) {
      const vendor = vendors?.find((mem) => mem.id === parseInt(newValue, 10));

      if (vendor) {
        newValue = vendor;
      }
    }

    if (e.target.name === 'startDate') {
      if (e.target.value.trim() === '') {
        setInputErrors((prevState) => ({
          ...prevState,
          startDate: 'Start Date is required',
        }));
      } else {
        setInputErrors((prevState) => ({
          ...prevState,
          startDate: '',
        }));
      }
    }

    if (e.target.name === 'dueDate') {
      if (e.target.value.trim() === '') {
        setInputErrors((prevState) => ({
          ...prevState,
          dueDate: 'Due Date is required',
        }));
      } else {
        setInputErrors((prevState) => ({
          ...prevState,
          dueDate: '',
        }));
      }
    }

    if (e.target.name === 'name') {
      if (e.target.value.trim() === '') {
        setInputErrors((prevState) => ({
          ...prevState,
          name: 'Deliverable name is required',
        }));
      } else if (e.target.value.length > 250) {
        setInputErrors((prevState) => ({
          ...prevState,
          name: 'Must be less than 250 characters',
        }));
      } else {
        setInputErrors((prevState) => ({
          ...prevState,
          name: '',
        }));
      }
    }

    setTouchedInput((prevState) => ({
      ...prevState,
      [e.target.name]: true,
    }));
    setDeliverableRow((row: DeliverableRowInterface) => ({
      ...row,
      [e.target.name]: newValue,
    }));
  };
  const saveTdmHandler = () => {
    const data: Partial<IPatchTdmPayload> = {
      ...(touchedInput?.name && { name: deliverableRow?.name }),
      ...(touchedInput?.status && { status: deliverableRow?.status }),
      ...(touchedInput?.lead && deliverableRow?.lead && { leadId: deliverableRow?.lead?.id }),
      ...(touchedInput?.support &&
        deliverableRow.support && {
          supportId: deliverableRow.support.id,
        }),
      ...(touchedInput?.vendor &&
        deliverableRow.vendor && {
          vendorId: deliverableRow.vendor.id,
        }),
      ...(touchedInput?.startDate && {
        startDate: deliverableRow.startDate ? formatDate3(deliverableRow.startDate) : '',
      }),
      ...(touchedInput?.dueDate && {
        dueDate: deliverableRow.dueDate ? formatDate3(deliverableRow.dueDate) : '',
      }),
    };

    if (inputErrors.name.length === 0 && inputErrors.dueDate.length === 0) {
      if (!isObjectEmpty(data)) {
        patchTdm.mutate(data);
      } else {
        editRowHandler();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'No changes were detected to save the deliverable.',
            toastType: 'info',
          })
        );
      }
    }
  };

  const deleteDeliverableHandler = (_row: any) => {
    setShowDeleteConfirm(true);
  };

  const deleteConfirmHandler = () => {
    removeTdm.mutate(deliverableRow.id);
  };

  const deleteCancelHandler = () => {
    setShowDeleteConfirm(false);
  };

  const tdmDetailViewHandler = () => {
    if (!applicationFormId || !applicationId) {
      return;
    }

    navigate(
      buildRoute(AppRoutes.ROVER_APPLICATION_FORM_APPLICATION_DELIVERABLE, {
        applicationFormId,
        applicationId,
        deliverableId: deliverable.id,
      })
    );
  };

  return (
    <>
      {showDeleteConfirm && (
        <Confirm
          title='Delete Deliverable'
          message={`You are about to delete ${deliverableRow?.name}. Are you sure you want to delete ?`}
          acceptButtonLabel={`${removeTdm.isLoading ? 'Deleting...' : 'Delete'}`}
          declineButtonLabel='Cancel'
          accept={deleteConfirmHandler}
          decline={deleteCancelHandler}
        />
      )}
      <tr
        key={deliverable.id}
        className={'w-full flex items-center justify-between px-5 py-4 border-b border-gray-200 text-sm hover:bg-green-50 cursor-pointer'}
      >
        {!deliverableRow.isEditing && (
          <td className='w-72 underline flex items-center'>
            <div onClick={tdmDetailViewHandler}>{deliverableRow.name}</div>
          </td>
        )}
        {deliverableRow.isEditing && (
          <td className='w-72 relative'>
            <Input type='text' defaultValue={deliverableRow.name} name='name' onChange={rowInputChangeHandler} />
            <span className='absolute text-xs text-tertiaryColor-normal'>{inputErrors?.name}</span>
          </td>
        )}
        <td className='w-36'>
          {!deliverableRow.isEditing && <Badge status={deliverableRow.status} text={deliverableStatus[deliverableRow.status]} />}
          {deliverableRow.isEditing && (
            <Select name='status' defaultValue={deliverableRow.status} onChange={rowInputChangeHandler}>
              {Object.keys(deliverableStatus)
                .filter((s) => s !== 'UNDER_REVIEW' && s !== 'UNDER_REVISION' && s !== 'COMPLETE')
                .map((key: string) => (
                  <option key={key} value={key}>
                    {deliverableStatus[key]}
                  </option>
                ))}
            </Select>
          )}
        </td>
        <td className='w-40'>
          {!deliverableRow.isEditing && deliverableRow.lead && (
            <Link to={`/app/administration/users/${deliverableRow?.lead?.id}`}>
              <div className='flex items-center gap-2'>
                <ImageCircularAvatar imageUrl={deliverableRow.lead.profileImageUrl} fallbackText={getInitials(deliverableRow.lead.name)} />
                <span>{deliverableRow.lead.name}</span>
              </div>
            </Link>
          )}
          {deliverableRow.isEditing && (
            <Select name='lead' defaultValue={deliverableRow.lead ? deliverableRow.lead.id : ''} onChange={rowInputChangeHandler}>
              {teams?.map((user: TdmMemberInterface) => {
                if (user.id !== deliverableRow?.support?.id) {
                  return (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  );
                }
                return <></>;
              })}
            </Select>
          )}
        </td>
        <td className='w-40'>
          {!deliverableRow.isEditing && deliverableRow.support && (
            <Link to={`/app/administration/users/${deliverableRow?.support?.id}`}>
              <div className='flex items-center gap-2'>
                <ImageCircularAvatar
                  imageUrl={deliverableRow.support.profileImageUrl}
                  fallbackText={getInitials(deliverableRow.support.name)}
                />
                <span>{deliverableRow.support.name}</span>
              </div>
            </Link>
          )}
          {!deliverableRow.isEditing && !deliverableRow.support && (
            <span>
              {deliverableRow.metaData.supportTitleId && getTitle(deliverableRow.metaData.supportTitleId)?.name}
              {deliverableRow.metaData.support && deliverableRow.metaData.support}
              {!deliverableRow.metaData.supportTitleId && !deliverableRow.metaData.support && 'Unassigned'}
            </span>
          )}
          {deliverableRow.isEditing && (
            <Select name='support' defaultValue={deliverableRow.support ? deliverableRow.support.id : ''} onChange={rowInputChangeHandler}>
              {!deliverableRow.support && <option>Select Support</option>}
              {teams?.map((user: TdmMemberInterface) => {
                if (user.id !== deliverableRow?.lead?.id) {
                  return (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  );
                }
                return <></>;
              })}
            </Select>
          )}
        </td>
        <td className='w-40'>
          {!deliverableRow.isEditing && deliverableRow?.vendor && <Link to='/app/vendor'>{deliverableRow?.vendor?.name}</Link>}

          {!deliverableRow.isEditing && !deliverableRow?.vendor && 'Unassigned'}

          {deliverableRow.isEditing && (
            <Select name='vendor' defaultValue={deliverableRow?.vendor?.id} onChange={rowInputChangeHandler}>
              {!deliverableRow.vendor && <option>Select Vendor</option>}
              {vendors?.map((user) => {
                return (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                );
              })}
            </Select>
          )}
        </td>
        {!deliverableRow.isEditing && <td className={'w-24'}>{deliverableRow.dueDate ? formatDate3(deliverableRow.dueDate) : 'None'}</td>}
        {deliverableRow.isEditing && (
          <td className={'w-36 relative'}>
            <Input
              type='date'
              defaultValue={deliverableRow.dueDate && formatDate3(deliverableRow.dueDate, 'yyyy-mm-dd')}
              name='dueDate'
              min={formatDate3(deliverableRow.startDate, 'yyyy-mm-dd')}
              onChange={rowInputChangeHandler}
            />
            <span className='absolute text-xs text-tertiaryColor-normal'>{inputErrors?.dueDate}</span>
          </td>
        )}
        {!deliverableRow.isEditing && (
          <td className='w-24 flex gap-2 justify-center'>
            <div
              id='edit-button'
              className='rounded-full p-2 text-neutralColor-normal hover:bg-neutralColor-lighter hover:text-primaryColor-normal transition duration-300 ease-in-out'
              onClick={editRowHandler}
            >
              <PencilIcon className='h-5 w-5' />
            </div>
            <div
              className='rounded-full p-2 text-neutralColor-normal hover:text-tertiaryColor-normal hover:bg-neutralColor-lighter transition duration-300 ease-in-out'
              onClick={deleteDeliverableHandler}
              id='delete-button'
            >
              <TrashIcon className='h-5 w-5' />
            </div>
          </td>
        )}
        {deliverableRow.isEditing && (
          <td className='w-24 flex gap-4'>
            <div
              id='save-button'
              className='bg-gray-100 rounded-full p-1 text-primaryColor-normal hover:bg-primaryColor-lightest transition duration-300 ease-in-out'
              onClick={saveTdmHandler}
            >
              <CheckIcon className='h-5 w-5' />
            </div>
            <div
              id='cancel-button'
              className='bg-gray-100 rounded-full p-1 text-tertiaryColor-normal hover:bg-tertiaryColor-lightest transition duration-300 ease-in-out'
              onClick={cancelEditHandler}
            >
              <XCircleIcon className='h-5 w-5' />
            </div>
          </td>
        )}
      </tr>
    </>
  );
}
